/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import * as THREE from 'three';
import { type IImporter, type ImportResult, type ImporterConfig, importNamesMapping } from './IImporter';
import type { MCSData } from './MCSD/m-c-s-data';
import type { IValue } from '../DataFlow/Graph';

export class MCSDImporter implements IImporter {
  private readonly _headerProvider: () => Promise<MCSData>;

  public constructor(headerProvider: () => Promise<MCSData>) {
    this._headerProvider = headerProvider;
  }

  public async import(result: ImportResult, config: ImporterConfig): Promise<void> {
    const header = await this._headerProvider();

    if (header.imagesLength() == 0) {
      throw new Error('Cases without images are not supported');
    }

    this._registerPhaseData(result, '{none}', '{unlinked}');

    const kindToIds: Map<string, Set<string>> = new Map();

    // Find out images kinds.
    // Herein we are assuming images are always containing at least one mesh.
    const meshes = importNamesMapping(config.meshes);
    for (let i = 0, sz = header.meshesLength(); i < sz; ++i) {
      const mesh = header.meshes(i)!;
      const label = mesh.label()!.toLowerCase();
      const values = meshes.get(label) || [];
      for (const value of values) {
        const imageId = mesh.imageGuid()!;
        if (imageId.length != 0 && value.imagePhaseKind) {
          this._registerPhaseData(result, imageId, value.imagePhaseKind);
          if (!kindToIds.has(value.imagePhaseKind)) {
            kindToIds.set(value.imagePhaseKind, new Set());
          }
          kindToIds.get(value.imagePhaseKind)!.add(imageId);
        }
      }
    }

    // Meshes
    for (let i = 0, sz = header.meshesLength(); i < sz; ++i) {
      const mesh = header.meshes(i)!;
      const guid = mesh.guid()!;
      const label = mesh.label()!.toLowerCase();
      const values = meshes.get(label) || [];
      for (const value of values) {
        const imageId = value.imagePhaseKind ? mesh.imageGuid()! : '';
        if (value.imagePhaseKind && imageId.length == 0) {
          result.unlinkedMeshes.set(guid, label);
        } else {
          this._processValue(
            result,
            imageId,
            label,
            guid,
            value.algoName,
            mesh,
            (mesh) => new DT.StringT(mesh.guid()!)
          );
        }
      }
    }

    // Points
    const points = importNamesMapping(config.points);
    for (let i = 0, sz = header.pointsLength(); i < sz; ++i) {
      const point = header.points(i)!;
      const guid = point.guid()!;
      const label = point.label()!.toLowerCase();
      const values = points.get(label) || [];
      for (const value of values) {
        const imageId = this._resolveImageId(kindToIds, point.imageGuid()!, value.imagePhaseKind);
        this._processValue(result, imageId, label, guid, value.algoName, point, (point) => {
          const coord = point.ptCoordinates()!;
          return new DT.Point(new THREE.Vector3(coord.x(), coord.y(), coord.z()));
        });
      }
    }

    // Splines
    const splines = importNamesMapping(config.splines);
    for (let i = 0, sz = header.splinesLength(); i < sz; ++i) {
      const spline = header.splines(i)!;
      const guid = spline.guid()!;
      const label = spline.label()!.toLowerCase();
      const values = splines.get(label) || [];
      for (const value of values) {
        const imageId = this._resolveImageId(kindToIds, spline.imageGuid()!, value.imagePhaseKind);
        this._processValue(result, imageId, label, guid, value.algoName, spline, (spline) => {
          const basePoints = new Array<THREE.Vector3>();
          for (let j = 0, cnt = spline.basePointsLength(); j < cnt; ++j) {
            const coord = spline.basePoints(j)!;
            basePoints.push(new THREE.Vector3(coord.x(), coord.y(), coord.z()));
          }
          return new DT.Polyline({ points: basePoints, isClosed: spline.isClosed() });
        });
      }
    }

    // Images
    for (let i = 0, sz = header.imagesLength(); i < sz; ++i) {
      const image = header.images(i)!;
      const guid = image.guid()!;
      const label = image.label()!;
      this._processImage(result, guid, label);
    }
    if ((result.phaseData.get('{none}')?.valuesCollection.size ?? -1) > 0) {
      this._processImage(result, '{none}', '{none}', false);
    } else {
      result.phaseData.delete('{none}');
    }
  }

  private _registerPhaseData(result: ImportResult, imageId: string, imagePhaseKind: string): void {
    if (!result.phaseData.has(imageId)) {
      result.phaseData.set(imageId, {
        images: [],
        imageKinds: new Set(),
        valuesCollection: new Map()
      });
    }
    result.phaseData.get(imageId)!.imageKinds.add(imagePhaseKind);
  }

  private _resolveImageId(
    kindToIds: Map<string, Set<string>>,
    guid: string,
    imagePhaseKind: string | undefined
  ): string {
    if (!imagePhaseKind) {
      return '';
    }
    if (guid.length == 0) {
      const images = kindToIds.get(imagePhaseKind);
      if (images?.size === 1) {
        return Array.from(images)[0];
      }
    }
    return guid;
  }

  private _processValue<T>(
    result: ImportResult,
    imageId: string,
    label: string,
    guid: string,
    name: string,
    value: T,
    valueGetter: (value: T) => IValue
  ): void {
    const _imageId = imageId.length != 0 ? imageId : '{none}';
    const valuesCollection = result.phaseData.get(_imageId)?.valuesCollection;
    if (valuesCollection) {
      if (valuesCollection.has(name)) {
        const duplicates = result.imageIdToDuplicateEntities.get(_imageId);
        if (!duplicates) {
          result.imageIdToDuplicateEntities.set(_imageId, [[label, guid]]);
        } else {
          duplicates.push([label, guid]);
        }
      } else {
        valuesCollection.set(name, valueGetter(value));
      }
    }
  }

  private _processImage(result: ImportResult, guid: string, label: string, shouldReportNoObjects = true): void {
    const data = result.phaseData.get(guid);
    if (!data) {
      if (shouldReportNoObjects) {
        result.imagesWithoutObjects.set(guid, label);
      }
    } else {
      for (const kind of data.imageKinds) {
        data.images.push({ kind: kind, label: label });
      }
    }
  }
}
